import type { PdfDocumentObjectV2 } from "@bezpart-board/shared";
import { PDF_DEFAULT_FRAME_WIDTH, PDF_MAX_PAGES } from "./constants";
import { openPdfDocument, type PdfRenderProgress, setPdfPagePreview, clearAllPdfPagePreviews } from "./pdfRenderPages";
import { runAsyncPool } from "./asyncPool";

export type ImportPdfProgress =
  | PdfRenderProgress
  | { phase: "uploading"; page: number; total: number }
  | { phase: "placing"; page: number; total: number };

export type ImportPdfOptions = {
  file: File;
  boardId: string;
  shareToken?: string | null;
  guestEditKey?: string | null;
  useCredentials: boolean;
  worldX: number;
  worldY: number;
  maxPages?: number;
  onProgress?: (p: ImportPdfProgress) => void;
  /** После первой страницы — объект можно показать на доске. */
  onObjectReady?: (object: PdfDocumentObjectV2) => void | Promise<void>;
  /** Промежуточное обновление pageAssetIds (миниатюры догружаются). */
  onPageAssetsUpdate?: (
    objectId: string,
    pageAssetIds: string[],
    pageCount: number
  ) => void | Promise<void>;
  /** Локальное превью страницы отрендерено — можно показать миниатюру. */
  onPagePreviewReady?: (objectId: string, pageIndex: number) => void | Promise<void>;
};

type UploadedAsset = { id: string };

const PAGE_UPLOAD_CONCURRENCY = 5;

async function uploadAsset(
  file: File | Blob,
  filename: string,
  opts: ImportPdfOptions
): Promise<UploadedAsset> {
  const form = new FormData();
  form.append("file", file, filename);
  form.append("boardId", opts.boardId);
  if (opts.shareToken) form.append("shareToken", opts.shareToken);

  let url = "/api/v1/assets/upload";
  if (opts.shareToken) url += `?t=${encodeURIComponent(opts.shareToken)}`;
  else if (opts.guestEditKey) url += `?k=${encodeURIComponent(opts.guestEditKey)}`;

  const res = await fetch(url, {
    method: "POST",
    credentials: opts.useCredentials ? "include" : "omit",
    body: form,
  });
  const data = (await res.json()) as { asset?: UploadedAsset; error?: string };
  if (!res.ok) throw new Error(data.error || "Upload failed");
  if (!data.asset?.id) throw new Error("Upload failed");
  return data.asset;
}

function frameSizeFromPage(pageW: number, pageH: number): { w: number; h: number } {
  const w = PDF_DEFAULT_FRAME_WIDTH;
  const h = Math.max(40, Math.round((pageH / pageW) * w));
  return { w, h };
}

function buildPdfObject(
  opts: ImportPdfOptions,
  params: {
    id: string;
    sourceAssetId: string;
    pageAssetIds: string[];
    pageCount: number;
    firstPageW: number;
    firstPageH: number;
  }
): PdfDocumentObjectV2 {
  const size = frameSizeFromPage(params.firstPageW, params.firstPageH);
  const title = opts.file.name.replace(/\.pdf$/i, "") || "Документ";
  return {
    id: params.id,
    type: "pdf_document",
    x: Math.round(opts.worldX - size.w / 2),
    y: Math.round(opts.worldY - size.h / 2),
    w: size.w,
    h: size.h,
    sourceAssetId: params.sourceAssetId,
    pageAssetIds: params.pageAssetIds,
    pageCount: params.pageCount,
    currentPage: 0,
    displayMode: "board",
    allowViewerPageNav: false,
    title,
    style: {
      borderColor: "#cbd5e1",
      borderWidth: 1,
    },
  };
}

/**
 * Upload PDF → render pages → upload PNGs → pdf_document.
 * Объект появляется после 1-й страницы; остальные страницы — параллельно.
 */
export async function importPdfToBoard(opts: ImportPdfOptions): Promise<PdfDocumentObjectV2> {
  const maxPages = opts.maxPages ?? PDF_MAX_PAGES;
  // Сервер разрешает столько же (ASSET_MAX_PDF_BYTES); проверка здесь — чтобы
  // не отправлять десятки мегабайт ради отказа.
  if (opts.file.size > 52_428_800) {
    throw new Error("PDF больше 50 МБ — уменьшите его или разбейте на части");
  }

  const objectId = crypto.randomUUID();
  opts.onProgress?.({ phase: "loading", page: 0, total: 0 });

  const pdf = await openPdfDocument(opts.file, { maxPages });
  const total = pdf.totalPages;
  if (!total) {
    pdf.destroy();
    throw new Error("PDF не содержит страниц");
  }

  try {
    opts.onProgress?.({ phase: "uploading", page: 0, total: 0 });
    const sourcePromise = uploadAsset(opts.file, opts.file.name, opts);

    opts.onProgress?.({ phase: "rendering", page: 1, total });
    const firstRendered = await pdf.renderPage(1);
    setPdfPagePreview(objectId, 0, firstRendered.blob);

    const remainingSlots: Array<{ pageNum: number; blob: Blob }> = [];
    const restRenderPromise =
      total > 1
        ? (async () => {
            for (let pageNum = 2; pageNum <= total; pageNum++) {
              opts.onProgress?.({ phase: "rendering", page: pageNum, total });
              const rendered = await pdf.renderPage(pageNum);
              setPdfPagePreview(objectId, pageNum - 1, rendered.blob);
              remainingSlots.push({ pageNum, blob: rendered.blob });
              if (opts.onPagePreviewReady) {
                await opts.onPagePreviewReady(objectId, pageNum - 1);
              }
            }
          })()
        : Promise.resolve();

    opts.onProgress?.({ phase: "uploading", page: 1, total });
    const [source, firstUploaded] = await Promise.all([
      sourcePromise,
      uploadAsset(firstRendered.blob, "page-1.png", opts),
    ]);
    const pageAssetIds: string[] = [firstUploaded.id];

    const initialObject = buildPdfObject(opts, {
      id: objectId,
      sourceAssetId: source.id,
      pageAssetIds: pageAssetIds.slice(),
      pageCount: total,
      firstPageW: firstRendered.width,
      firstPageH: firstRendered.height,
    });

    opts.onProgress?.({ phase: "placing", page: 1, total });
    if (opts.onObjectReady) {
      await opts.onObjectReady(initialObject);
    }

    if (total === 1) {
      clearAllPdfPagePreviews(objectId);
      opts.onProgress?.({ phase: "done", page: 1, total: 1 });
      return initialObject;
    }

    await restRenderPromise;

    let uploadedCount = 1;
    const restIds = await runAsyncPool(
      remainingSlots,
      PAGE_UPLOAD_CONCURRENCY,
      async (entry) => {
        const asset = await uploadAsset(entry.blob, `page-${entry.pageNum}.png`, opts);
        uploadedCount += 1;
        opts.onProgress?.({ phase: "uploading", page: uploadedCount, total });
        return { pageNum: entry.pageNum, id: asset.id };
      }
    );

    restIds.sort((a, b) => a.pageNum - b.pageNum);
    for (const item of restIds) {
      pageAssetIds.push(item.id);
    }

    opts.onProgress?.({ phase: "done", page: total, total });

    const finalObject = buildPdfObject(opts, {
      id: objectId,
      sourceAssetId: source.id,
      pageAssetIds,
      pageCount: total,
      firstPageW: firstRendered.width,
      firstPageH: firstRendered.height,
    });

    if (opts.onPageAssetsUpdate && pageAssetIds.length > 1) {
      await opts.onPageAssetsUpdate(objectId, pageAssetIds.slice(), total);
    }

    clearAllPdfPagePreviews(objectId);
    return finalObject;
  } finally {
    pdf.destroy();
  }
}
